
import { Component, Prop, Provide } from "vue-property-decorator";
import { mapState } from "vuex";
import RequestPromises from "@/mixins/requestPromises";
import { IBulkAction } from "@/models/bulkactions";
import { INotificationTemplate } from "@/models/notifications";
import { DataModules } from "@/store/modules/data";
import { IState } from "@/store";
import {
  IBulkActionProvider,
  IBulkActionProviderMethods
} from "@/models/providers/bulkAction";
import { ISegmentTypes } from "@/data/enums/segments";
import { IProvider } from "@/models/providers";
import { CreateElement } from "vue";

@Component({
  computed: {
    ...mapState({
      bulkActionScope(state: IState, getters) {
        return getters[`data/${DataModules.BULK_ACTIONS}/scope`](
          this.bulkActionId
        );
      },
      templateScope(state: IState, getters) {
        return getters[`data/${DataModules.TEMPLATE}/scope`](
          this.$_.get(this.bulkAction, "action_data.template_id")
        );
      },
      bulkAction(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.BULK_ACTIONS,
          scope: this.bulkActionScope
        });
      },
      template(state: IState, getters) {
        return getters[`data/single`]({
          storeModule: DataModules.TEMPLATE,
          scope: this.templateScope,
          id: this.$_.get(this.bulkAction, "action_data.template_id")
        });
      }
    })
  }
})
export default class BulkActionProvider extends RequestPromises
  implements IProvider {
  // Props

  @Prop({ required: true })
  public bulkActionId!: ISegmentTypes;

  @Prop({ required: true })
  public segmentType!: ISegmentTypes;

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: undefined })
  public onNotFound!: IProvider["onNotFound"];

  @Prop({ default: true })
  public handleRequestError!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  // Data

  public bulkAction!: IBulkAction;
  public bulkActionScope!: string;
  public hasRequestError = false;
  public isLoading = true;
  public isReloading = false;
  public template!: INotificationTemplate;
  public templateScope!: string;

  // Life-cycle Methods

  public created() {
    this.loadData();
  }

  public beforeDestroy() {
    this.binTemplate();
    this.binBulkAction();
  }

  // Getters

  public get canViewBulkAction() {
    return this.$userCan(
      "get_bulk_action",
      "list_segments",
      "list_notification_templates"
    );
  }

  public get isReadonlyView() {
    return !["bulk_action_scheduled", "bulk_action_draft"].includes(
      this.$_.get(this.bulkAction, "status.code")
    );
  }

  // Methods

  public async loadData() {
    try {
      this.isLoading = true;
      await this.getBulkAction(this.bulkActionId);
      await this.getTemplate().catch(() => {
        // Mask error when template not found
      });
    } catch (error) {
      const is403 = this.$store.getters["api/is403"](error);
      const is404 = this.$store.getters["api/is404"](error);
      if ((is403 || is404) && this.onNotFound) this.onNotFound(this);
      this.hasRequestError = true;
      this.$store.dispatch("api/handleError", error);
    } finally {
      this.isReloading = false;
      this.isLoading = false;
    }
  }

  public async reloadData() {
    this.isReloading = true;
    await this.getBulkAction(this.bulkActionId);
  }

  public async getBulkAction(bulkActionId: IBulkAction["id"]) {
    if (!bulkActionId || !this.canViewBulkAction) return;
    await this.$store.dispatch(`data/${DataModules.BULK_ACTIONS}/get`, {
      id: bulkActionId,
      scope: this.bulkActionScope,
      vm: this,
      params: {
        with: "segment"
      }
    });
  }

  public async getTemplate() {
    return this.$store.dispatch(`data/${DataModules.TEMPLATE}/get`, {
      id: this.$_.get(this.bulkAction, "action_data.template_id"),
      scope: this.templateScope,
      vm: this
    });
  }

  public async binBulkAction() {
    return this.$store.dispatch("data/bin", {
      storeModule: DataModules.BULK_ACTIONS,
      scope: this.bulkActionScope,
      vm: this
    });
  }

  public async binTemplate() {
    return this.$store.dispatch("data/bin", {
      storeModule: DataModules.TEMPLATE,
      scope: this.templateScope,
      vm: this
    });
  }

  public openAddEditModal() {
    return this.$store.dispatch(
      `data/${DataModules.BULK_ACTIONS}/openAddEditModal`,
      {
        props: {
          bulkAction: null
        }
      }
    );
  }

  public openSegmentResultsModal() {
    if (!this.bulkAction.segment) return;
    this.$store.dispatch("ui/open/slideModal", {
      config: {
        component: () =>
          import("@/components/app/admin/segments/segmentResultsModal.vue"),
        props: {
          segmentId: this.bulkAction.segment_id,
          segmentType: this.$_.get(this.bulkAction, "segment.type", "")
        }
      }
    });
  }

  // Provide
  @Provide("bulkActionProvider")
  public providerData(): IBulkActionProvider {
    return {
      bulkAction: this.bulkAction,
      canViewBulkAction: this.canViewBulkAction,
      hasRequestError: this.hasRequestError,
      isLoading: this.isLoading,
      isReadonlyView: this.isReadonlyView,
      isReloading: this.isReloading,
      template: this.template
    };
  }

  @Provide("bulkActionProviderMethods")
  public get providerMethods(): IBulkActionProviderMethods {
    return {
      openAddEditModal: this.openAddEditModal,
      openSegmentResultsModal: this.openSegmentResultsModal,
      reloadData: this.reloadData
    };
  }

  // Life-cycle methods

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isLoading && this.hasRequestError },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $bulkActionData: this.providerData(),
        $bulkActionMethods: this.providerMethods
      })
    );
  }
}
